import { Checkbox, CheckboxGroup } from "@gigpro/ui/Checkbox";
import { Label } from "@gigpro/ui/Label";
import { pluralize } from "@gigpro/utils";
import { convertToTimezone, formatDate, formatTime } from "@gigpro/utils/datetime";
import type { ConvertibleShift } from "@sidegigllc/api/client";
import { SeatsForCandidates } from "domains/hire/components/SeatsForCandidates";
import { openSeatsOf, seatsFor } from "./seats";

export const countOf = (count: number, singular: string) => `${count} ${pluralize(count, singular)}`;

const shiftNotes = (shift: ConvertibleShift) => {
  const open = openSeatsOf(shift);
  return [
    `${countOf(open, "open seat")} for working interviews`,
    shift.confirmed_count > 0 &&
      `${countOf(shift.confirmed_count, "confirmed Pro")} ${shift.confirmed_count === 1 ? "stays" : "stay"} booked on the regular shift`,
    shift.outstanding_offers_count > 0 && `${countOf(shift.outstanding_offers_count, "open offer")} will be withdrawn`,
    shift.in_series && "Leaves its recurring series",
    shift.in_bundle && "The open seats leave the bundle and its bonus",
  ].filter((note): note is string => Boolean(note));
};

interface ShiftOptionProps {
  shift: ConvertibleShift;
  timezone: string | null;
  picked: boolean;
  seats: number;
  onSeatsChange: (seats: number) => void;
}

const ShiftOption = ({ shift, timezone, picked, seats, onSeatsChange }: ShiftOptionProps) => {
  const start = convertToTimezone(shift.start_time, timezone);
  const end = convertToTimezone(shift.end_time, timezone);
  const id = `convert-shift-${shift.gig_id}`;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-light-gray bg-white p-3">
      <Label htmlFor={id} className="flex items-start gap-3">
        <Checkbox id={id} value={String(shift.gig_id)} className="mt-0.5" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-normal font-semibold text-black">
            {formatDate(start, "EEE, MMM d")} · {formatTime(start)} – {formatTime(end)}
          </span>
          <span className="text-small text-gray">{shift.position_name}</span>
          <ul className="mt-1 flex flex-col gap-0.5 font-regular">
            {shiftNotes(shift).map((note) => (
              <li key={note} className="text-small text-black">
                {note}
              </li>
            ))}
          </ul>
        </div>
      </Label>
      {/* Outside the label, so a click on Change or the stepper doesn't toggle the pick. */}
      {picked && (
        <div className="pl-7">
          <SeatsForCandidates openSeats={openSeatsOf(shift)} value={seats} onChange={onSeatsChange} />
        </div>
      )}
    </div>
  );
};

interface ConvertibleShiftListProps {
  shifts: Array<ConvertibleShift>;
  timezone: string | null;
  /** Picked gig ids, as strings. */
  value: Array<string>;
  onValueChange: (value: Array<string>) => void;
  /** Seats chosen per gig id; a shift not in it gives its default of one. */
  seats: Record<number, number>;
  onSeatsChange: (gigId: number, seats: number) => void;
}

/** Shifts that can become working interviews, each saying what converting it changes. */
export const ConvertibleShiftList = ({
  shifts,
  timezone,
  value,
  onValueChange,
  seats,
  onSeatsChange,
}: ConvertibleShiftListProps) => (
  <fieldset aria-label="Shifts to convert" className="min-w-0">
    <CheckboxGroup className="max-h-96 overflow-auto" value={value} onValueChange={onValueChange}>
      {shifts.map((shift) => (
        <ShiftOption
          key={shift.gig_id}
          shift={shift}
          timezone={timezone}
          picked={value.includes(String(shift.gig_id))}
          seats={seatsFor(shift, seats)}
          onSeatsChange={(next) => onSeatsChange(shift.gig_id, next)}
        />
      ))}
    </CheckboxGroup>
  </fieldset>
);

/** The picked shifts, in list order; ids no longer listed (the shift changed) drop out. */
export const pickedShifts = (shifts: Array<ConvertibleShift>, value: Array<string>) =>
  shifts.filter((shift) => value.includes(String(shift.gig_id)));
